import { BUSY_BUFFER_CHOICES, type BusyCalendarChoice, DEFAULT_BUSY_BUFFER, type GoogleCalendarChoice, MAX_ICAL_ADDRESSES, type StaffBusyInput, type StaffCalendarInput, type StaffCalendarView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { del, errorMessage, get, patch, post, put } from "../api";
import { Alert, Button, Checkbox, Field, Loading, Panel, PanelHeader, formatDateTime } from "./ui";

/**
 * Visits on a phone calendar. The clinic's own Google accounts (under its BAA) get the client's name and
 * service, on the Google calendar they pick; any other calendar subscribes to a private link that shows only
 * "Vaulted visit" and the times. Optionally, busy times on their own calendars block booking.
 */
export function StaffCalendarPanel({ staffId, title, description, className }: { staffId: string; title: string; description: string; className?: string }) {
  const qc = useQueryClient();
  const key = ["staff-calendar", staffId];
  const q = useQuery({ queryKey: key, queryFn: () => get<StaffCalendarView>(`/api/staff/${staffId}/calendar`) });
  const c = q.data;
  const [email, setEmail] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [icalUrl, setIcalUrl] = useState("");
  useEffect(() => setEmail(c?.googleEmail ?? ""), [c?.googleEmail]);

  // Their Google calendars (when their clinic Google account is saved).
  const googleReady = !!c?.google && !!c.googleEmail?.endsWith(`@${c.google.domain}`);
  const calKey = ["staff-calendar-google", staffId, c?.googleEmail];
  const cals = useQuery({ queryKey: calKey, queryFn: () => get<{ calendars: GoogleCalendarChoice[] }>(`/api/staff/${staffId}/calendar/google`), enabled: googleReady, retry: false, staleTime: 60_000 });
  const list = cals.data?.calendars ?? [];

  const setView = (v: StaffCalendarView) => qc.setQueryData(key, v);
  const save = useMutation({ mutationFn: (v: StaffCalendarInput) => put<StaffCalendarView>(`/api/staff/${staffId}/calendar`, v), onSuccess: setView });
  const makeCalendar = useMutation({
    mutationFn: () => post<StaffCalendarView>(`/api/staff/${staffId}/calendar/google`),
    onSuccess: (v) => {
      setView(v);
      void qc.invalidateQueries({ queryKey: calKey });
    },
  });
  const makeLink = useMutation({
    mutationFn: () => post<{ url: string; view: StaffCalendarView }>(`/api/staff/${staffId}/calendar/feed`),
    onSuccess: (r) => {
      setLink(r.url);
      setCopied(false);
      setView(r.view);
    },
  });
  const linkOff = useMutation({
    mutationFn: () => del<StaffCalendarView>(`/api/staff/${staffId}/calendar/feed`),
    onSuccess: (v) => {
      setLink(null);
      setView(v);
    },
  });
  const busy = useMutation({ mutationFn: (v: StaffBusyInput) => put<StaffCalendarView>(`/api/staff/${staffId}/calendar/busy`, v), onSuccess: setView });
  const addIcal = useMutation({
    mutationFn: (url: string) => post<StaffCalendarView>(`/api/staff/${staffId}/calendar/busy/ical`, { url }),
    onSuccess: (v) => {
      setIcalUrl("");
      setView(v);
    },
  });
  const removeIcal = useMutation({ mutationFn: (id: string) => del<StaffCalendarView>(`/api/staff/${staffId}/calendar/busy/ical/${id}`), onSuccess: setView });

  const patchIcal = useMutation({
    mutationFn: (v: { id: string; before: number; after: number }) => patch<StaffCalendarView>(`/api/staff/${staffId}/calendar/busy/ical/${v.id}`, { before: v.before, after: v.after }),
    onSuccess: setView,
  });

  // Google calendars ticked for busy times (with their minutes before and after): their main one by default, until they've chosen.
  const [ticked, setTicked] = useState<BusyCalendarChoice[]>([]);
  const savedTicks = JSON.stringify(c?.busyCalendars ?? []);
  useEffect(() => {
    if (!c) return;
    if (c.busyCalendars.length || c.blockBusy) setTicked(c.busyCalendars);
    else {
      const main = list.find((x) => x.primary);
      setTicked(main ? [{ id: main.id, before: DEFAULT_BUSY_BUFFER, after: DEFAULT_BUSY_BUFFER }] : []);
    }
  }, [savedTicks, c?.blockBusy, list.length]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const googleInput = (more: Partial<StaffCalendarInput> = {}): StaffCalendarInput => ({ googleEmail: email.trim().toLowerCase(), pushGoogle: c?.pushGoogle ?? false, ...more });
  const setTicks = (next: BusyCalendarChoice[]) => {
    setTicked(next);
    // Already on: save the change now (off when nothing is left to read).
    if (c?.blockBusy) busy.mutate({ on: next.length > 0 || c.icals.length > 0, googleCalendars: next });
  };
  const tick = (id: string, on: boolean) => setTicks(on ? [...ticked, { id, before: DEFAULT_BUSY_BUFFER, after: DEFAULT_BUSY_BUFFER }] : ticked.filter((x) => x.id !== id));
  const error = save.error ?? makeCalendar.error ?? makeLink.error ?? linkOff.error ?? busy.error ?? addIcal.error ?? removeIcal.error ?? patchIcal.error;

  return (
    <Panel className={className}>
      <PanelHeader title={title} description={description} />
      <div className="space-y-6 px-5 py-5 text-sm">
        {!c ? (
          q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Loading />
        ) : (
          <>
            <section>
              <h3 className="font-semibold text-ink">Google Calendar (clinic account)</h3>
              {!c.google ? (
                <p className="mt-1 text-muted">Not connected for the clinic yet.</p>
              ) : (
                <>
                  <p className="mt-1 text-muted">
                    For @{c.google.domain} accounts only: each visit shows the client's full name and service, and updates when it's moved or cancelled.
                  </p>
                  <div className="mt-3 flex max-w-xl flex-wrap items-end gap-3">
                    <Field className="min-w-60 flex-1" label="Google account" type="email" value={email} placeholder={`name@${c.google.domain}`} onChange={(e) => setEmail(e.target.value)} />
                    {email.trim().toLowerCase() !== (c.googleEmail ?? "") && (
                      <Button busy={save.isPending} onClick={() => save.mutate(googleInput())}>
                        Save
                      </Button>
                    )}
                  </div>
                  <div className="mt-2">
                    <Checkbox label="Put my visits on Google Calendar" checked={c.pushGoogle} disabled={save.isPending} onChange={(on) => save.mutate(googleInput({ pushGoogle: on }))} />
                  </div>
                  {c.pushGoogle && googleReady && (
                    <div className="mt-2 max-w-xl pl-2">
                      <label className="mb-1 block text-sm font-semibold text-ink-soft" htmlFor={`cal-${staffId}`}>
                        Which calendar
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          id={`cal-${staffId}`}
                          className="min-w-60 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2"
                          value={c.pushCalendarId ?? ""}
                          disabled={save.isPending || cals.isLoading}
                          onChange={(e) => save.mutate(googleInput({ pushCalendarId: e.target.value || null }))}
                        >
                          <option value="">My main calendar</option>
                          {list
                            .filter((x) => x.canWrite && !x.primary)
                            .map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name}
                              </option>
                            ))}
                          {c.pushCalendarId && !list.some((x) => x.id === c.pushCalendarId) && <option value={c.pushCalendarId}>{cals.isLoading ? "Loading…" : "A calendar no longer in their list"}</option>}
                        </select>
                        <Button variant="quiet" busy={makeCalendar.isPending} onClick={() => makeCalendar.mutate()}>
                          + New calendar just for visits
                        </Button>
                      </div>
                      {cals.error && <p className="mt-1 text-danger">{errorMessage(cals.error)}</p>}
                      <p className="mt-1 text-muted">Changing it moves the visits over within a couple of minutes.</p>
                    </div>
                  )}
                  {c.pushGoogle && (
                    <p className="mt-2 text-muted">
                      {c.googleError ? <span className="text-danger">Last try failed: {c.googleError}</span> : c.googleSyncedAt ? `Up to date as of ${formatDateTime(c.googleSyncedAt)}.` : "Visits appear within a couple of minutes."}
                    </p>
                  )}
                </>
              )}
            </section>

            <section>
              <h3 className="font-semibold text-ink">Any other calendar (subscribe link)</h3>
              <p className="mt-1 text-muted">Shows only "Vaulted visit" and the times, no client details. Keep the link private: anyone with it can see those times.</p>
              {link ? (
                <div className="mt-3 max-w-xl space-y-2">
                  <input readOnly value={link} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2 font-mono text-xs" aria-label="Subscribe link" />
                  <div className="flex gap-2">
                    <Button onClick={copy}>{copied ? "Copied" : "Copy link"}</Button>
                    <a className="inline-flex items-center rounded-[var(--radius-control)] px-4 py-2 text-sm font-semibold text-ink-soft hover:bg-mist" href={link.replace(/^https:/, "webcal:")}>
                      Open in Calendar
                    </a>
                  </div>
                  <p className="text-muted">
                    This is shown once. iPhone: tap Open in Calendar, or Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar. Google: calendar.google.com → Other calendars → + → From URL. Outlook: Add calendar → Subscribe from web.
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-muted">{c.hasFeed ? `A link is on (made ${formatDateTime(c.feedCreatedAt)}). A new link turns the old one off.` : "No link yet."}</p>
              )}
              <div className="mt-3 flex gap-2">
                <Button busy={makeLink.isPending} onClick={() => makeLink.mutate()}>
                  {c.hasFeed ? "New link" : "Make a link"}
                </Button>
                {c.hasFeed && (
                  <Button variant="danger" busy={linkOff.isPending} onClick={() => linkOff.mutate()}>
                    Turn link off
                  </Button>
                )}
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-ink">Block my busy times (optional)</h3>
              <p className="mt-1 text-muted">
                Times you're marked Busy on the calendars below block booking, like time off, plus the minutes before and after you choose for each calendar (time to get there and back). All-day events, events marked Free, and declined invitations don't count. Checked every 10 minutes.
              </p>

              {googleReady && (
                <div className="mt-3">
                  <p className="font-medium text-ink-soft">Google calendars</p>
                  {cals.isLoading ? (
                    <Loading />
                  ) : cals.error ? (
                    <p className="mt-1 text-danger">{errorMessage(cals.error)}</p>
                  ) : (
                    <div className="mt-1 grid max-w-xl gap-0.5">
                      {list
                        .filter((x) => x.canRead)
                        .map((x) => {
                          const t = ticked.find((y) => y.id === x.id);
                          return (
                            <div key={x.id} className="flex flex-wrap items-center justify-between gap-x-3">
                              <Checkbox
                                label={x.primary ? `${x.name} (main)` : x.name}
                                description={x.id === c.pushCalendarId ? "Your visits calendar: Vaulted's own visits never count." : undefined}
                                checked={!!t}
                                disabled={busy.isPending}
                                onChange={(on) => tick(x.id, on)}
                              />
                              {t && <BufferPicker value={t} disabled={busy.isPending} onChange={(v) => setTicks(ticked.map((y) => (y.id === x.id ? { ...y, ...v } : y)))} />}
                            </div>
                          );
                        })}
                      <p className="text-muted">A personal calendar shared with your @{c.google!.domain} account shows up here too.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 max-w-xl">
                <p className="font-medium text-ink-soft">Calendar addresses (iCal)</p>
                {c.icals.length > 0 && (
                  <ul className="mt-1 divide-y divide-mist rounded-[var(--radius-control)] border border-line">
                    {c.icals.map((x) => (
                      <li key={x.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                        <span>
                          {x.host || "Calendar address"} <span className="text-muted">· added {formatDateTime(x.addedAt)}</span>
                        </span>
                        <BufferPicker value={x} disabled={patchIcal.isPending} onChange={(v) => patchIcal.mutate({ id: x.id, ...v })} />
                        <Button variant="quiet" busy={removeIcal.isPending && removeIcal.variables === x.id} onClick={() => removeIcal.mutate(x.id)}>
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                {c.icals.length < MAX_ICAL_ADDRESSES && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <Field className="min-w-60 flex-1" label="Add a calendar's private address" value={icalUrl} placeholder="https://… or webcal://…" onChange={(e) => setIcalUrl(e.target.value)} />
                    <Button busy={addIcal.isPending} disabled={!icalUrl.trim()} onClick={() => addIcal.mutate(icalUrl.trim())}>
                      Add
                    </Button>
                  </div>
                )}
                <p className="mt-1 text-muted">
                  Google: calendar settings → the calendar → Integrate calendar → Secret address in iCal format. iPhone/iCloud: share the calendar as a Public Calendar and copy its link. Outlook: Settings → Calendar → Shared calendars → Publish a calendar → ICS link. Saved encrypted; never shown again.
                </p>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {c.blockBusy ? (
                  <Button variant="danger" busy={busy.isPending} onClick={() => busy.mutate({ on: false, googleCalendars: ticked })}>
                    Stop blocking busy times
                  </Button>
                ) : (
                  <Button variant="primary" busy={busy.isPending} disabled={!ticked.length && !c.icals.length} onClick={() => busy.mutate({ on: true, googleCalendars: ticked })}>
                    Block my busy times
                  </Button>
                )}
              </div>
              {c.blockBusy && (
                <p className="mt-2 text-muted">
                  On.{" "}
                  {c.busyError ? <span className="text-danger">Last read had a problem: {c.busyError}</span> : c.busySyncedAt ? `Last read ${formatDateTime(c.busySyncedAt)}.` : "First read within a minute."}
                </p>
              )}
            </section>
          </>
        )}
        {error && <Alert>{errorMessage(error)}</Alert>}
      </div>
    </Panel>
  );
}

/** Minutes blocked before and after a calendar's events. */
function BufferPicker({ value, disabled, onChange }: { value: { before: number; after: number }; disabled?: boolean; onChange: (v: { before: number; after: number }) => void }) {
  const select = (which: "before" | "after", label: string) => (
    <label className="flex items-center gap-1 text-muted">
      {label}
      <select
        className="rounded-[var(--radius-control)] border border-line bg-white px-1.5 py-1 text-ink"
        value={value[which]}
        disabled={disabled}
        onChange={(e) => onChange({ before: value.before, after: value.after, [which]: Number(e.target.value) })}
      >
        {BUSY_BUFFER_CHOICES.map((m) => (
          <option key={m} value={m}>
            {m} min
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <span className="flex items-center gap-2 text-xs">
      {select("before", "Before")}
      {select("after", "After")}
    </span>
  );
}
